import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress,
  Alert, Snackbar, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, Tooltip, LinearProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import { useNavigate } from 'react-router-dom';
import {
  collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import * as pdfjsLib from 'pdfjs-dist';
import { COLORS } from '../theme/constants';
import { parseRefBound } from '../utils/parameterEvaluator';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const SPECIES_ALIAS = {
  canino: 'perro', felino: 'gato', equino: 'equino', bovino: 'bovino', aviar: 'aviar',
};

const norm = (s) =>
  (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

// ── Mapeo código analizador → nombre clínico estándar ──────────────────────
const PARAM_MAP = {
  'ALB (SER)':        { name: 'ALBUMINA',             unit: 'g/dL'  },
  'ALP-AMP (SER)':    { name: 'FOSFATASAS ALCALINAS', unit: 'U/L'   },
  'ALT (SER)':        { name: 'GPT/ALT',              unit: 'U/L'   },
  'AST-GOT (SER)':    { name: 'GOT/AST',              unit: 'U/L'   },
  'TBIL DPD (SER)':   { name: 'BILIRRUBINA TOTAL',    unit: 'mg/dL' },
  'CA AZO (SER)':     { name: 'CALCIO',               unit: 'mg/dL' },
  'CHOL (SER)':       { name: 'COLESTEROL TOTAL',     unit: 'mg/dL' },
  'CREA (SER)':       { name: 'CREATININA',           unit: 'mg/dL' },
  'GGT (SER)':        { name: 'GGT',                  unit: 'U/L'   },
  'GLU (SER)':        { name: 'GLICEMIA',             unit: 'mg/dL' },
  'PHOS (SER)':       { name: 'FOSFORO',              unit: 'mg/dL' },
  'T-PROT B (SER)':   { name: 'PROTEINAS TOTALES',   unit: 'g/dL'  },
  'UREA (SER)':       { name: 'UREMIA',               unit: 'mg/dL' },
};

// ── Parser PDF bioquímica ───────────────────────────────────────────────────
// Group text items by Y coordinate with tolerance
function groupByY(items, tolerance = 5) {
  const rows = [];
  items.filter(i => i.str.trim()).forEach(item => {
    const y = item.transform[5];
    const x = item.transform[4];
    const existing = rows.find(r => Math.abs(r.y - y) <= tolerance);
    if (existing) {
      existing.cells.push({ text: item.str.trim(), x });
    } else {
      rows.push({ y, cells: [{ text: item.str.trim(), x }] });
    }
  });
  rows.forEach(r => r.cells.sort((a, b) => a.x - b.x));
  rows.sort((a, b) => b.y - a.y);
  return rows;
}

// Merge cells that together form a decimal number split by the PDF renderer
// e.g. [{text:'3,'}, {text:'28'}] → [{text:'3,28'}]
function mergeSplitDecimals(cells) {
  const out = [];
  for (let i = 0; i < cells.length; i++) {
    const cur = cells[i];
    const nxt = cells[i + 1];
    if (nxt && /^\d+[,\.]$/.test(cur.text) && /^\d/.test(nxt.text) && (nxt.x - cur.x) < 25) {
      out.push({ text: cur.text + nxt.text, x: cur.x });
      i++;
    } else if (/^[,\.]\d+$/.test(cur.text) && out.length > 0 && /\d$/.test(out[out.length - 1].text)) {
      out[out.length - 1].text += cur.text;
    } else {
      out.push(cur);
    }
  }
  return out;
}

function findNearestCol(colDefs, x) {
  return colDefs.reduce((best, c) =>
    Math.abs(c.x - x) < Math.abs(best.x - x) ? c : best
  );
}

// Normalize a column header text to a PARAM_MAP key
function resolveParamKey(colText) {
  const normalized = colText
    .replace(/\s+/g, ' ')
    .replace(/\s+-\s+/g, '-')
    .trim();
  const keys = Object.keys(PARAM_MAP);
  if (keys.includes(normalized)) return normalized;
  return normalized;
}

function splitHeaderText(headerText) {
  const tokens = headerText.split(' ').filter(Boolean);
  const cols = [];
  let acc = '';
  tokens.forEach(t => {
    if (acc) acc += ' ' + t;
    else acc = t;
    const normalized = acc
      .replace(/\s+/g, ' ')
      .replace(/\s+-\s+/g, '-')
      .trim();
    if (PARAM_MAP[normalized] || /(\(SER\)|\(URI\))$/i.test(normalized)) {
      cols.push(normalized);
      acc = '';
    }
  });
  if (acc) {
    const normalized = acc
      .replace(/\s+/g, ' ')
      .replace(/\s+-\s+/g, '-')
      .trim();
    cols.push(normalized);
  }
  return cols;
}

async function parseBiochemPDF(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const data = {}; // orderId → { paramKey: value }

  for (let p = 1; p <= pdfDoc.numPages; p++) {
    const page = await pdfDoc.getPage(p);
    const tc   = await page.getTextContent();
    const rows = groupByY(tc.items, 5);

    console.log(`[BulkLabImport] Page ${p}: ${rows.length} rows detected`);

    // ── 1. Find header rows (contain 'Paciente' or known param codes) ────────
    const headerCells = [];
    let headerJoined = '';
    let dataStartIdx  = -1;
    let inHeader      = false;

    for (let ri = 0; ri < rows.length; ri++) {
      const joined = rows[ri].cells.map(c => c.text).join(' ');

      const hasPaciente = joined.includes('Paciente');
      const hasParamCode = Object.keys(PARAM_MAP).some(k =>
        joined.includes(k.replace(' (SER)', '')) // e.g. 'ALB', 'GLU', 'UREA'
      );

      if (hasPaciente || hasParamCode) {
        inHeader = true;
        if (headerJoined === '') {
          headerJoined = joined.replace(/Paciente\b\s*/, '').trim();
        }
        rows[ri].cells.forEach(c => {
          if (c.text !== 'Paciente') headerCells.push(c);
        });
        console.log(`[BulkLabImport] P${p} header row ${ri}:`, joined);
      } else if (inHeader && dataStartIdx === -1) {
        // First non-header row after header section
        dataStartIdx = ri;
      }
    }

    if (headerCells.length === 0) {
      console.warn(`[BulkLabImport] Page ${p}: no header detected, skipping`);
      continue;
    }
    if (dataStartIdx === -1) dataStartIdx = rows.findIndex(r =>
      r.cells.map(c => c.text).join(' ').match(/\b\d{4}\b/));
    if (dataStartIdx === -1) continue;

    // ── 2. Build column definitions from header text and data row positions ────────
    // The header may be rendered as a single long string; split it into
    // individual column keys, then assign x positions from the first data
    // row that has at least as many value cells as header columns.
    const headerParams = splitHeaderText(headerJoined);
    const colDefs = [];
    for (let pri = dataStartIdx; pri < rows.length; pri++) {
      const candidate = mergeSplitDecimals(rows[pri].cells);
      const valueCells = candidate
        .filter(c => {
          const stripped = c.text
            .replace(/\s+(g\/[dDlL]+|U\/L|mg\/[dDlL]+|g\/L|mmol\/L|µmol\/L|IU\/L|%)/i, '')
            .trim();
          return /^\d+[,\.]\d+$/.test(stripped) || /^\d{1,3}$/.test(stripped) || stripped === '-';
        })
        .sort((a, b) => a.x - b.x);
      if (valueCells.length >= headerParams.length) {
        headerParams.forEach((text, i) => {
          if (valueCells[i]) colDefs.push({ text, x: valueCells[i].x });
        });
        break;
      }
    }
    console.log(`[BulkLabImport] P${p} colDefs:`, colDefs.map(c => `${c.text}@${Math.round(c.x)}`));

    // ── 3. Parse data rows ───────────────────────────────────────────────────
    for (let ri = dataStartIdx; ri < rows.length; ri++) {
      const rawCells = rows[ri].cells;
      const fullLine = rawCells.map(c => c.text).join(' ');

      // Must contain a 4-digit order identifier
      const m = fullLine.match(/\b(\d{4})\b/);
      if (!m) continue;
      const orderId = m[1];

      // Skip repeated-measurement rows (e.g. "ORUGO GLU - - - - - -")
      const lettersOnly = fullLine.replace(/[\d\s,\.\-]/g, '').trim();
      if (lettersOnly.length > 0 && !lettersOnly.replace(/GLU|UREA|ORUGO|TADEO|[A-Z]/g, '').trim())
        if (/GLU$|UREA$/.test(lettersOnly)) continue;

      if (!data[orderId]) data[orderId] = {};

      // Merge split decimal cells before matching
      const cells = mergeSplitDecimals(rawCells);

      console.log(`[BulkLabImport] P${p} row ${ri} (id=${orderId}):`,
        cells.map(c => c.text).join(' | '));

      cells.forEach(cell => {
        if (cell.text === orderId) return;

        // Strip unit suffix so "3,28 g/dL" → "3,28", "70 mg/dL" → "70"
        const stripped = cell.text
          .replace(/\s+(g\/[dDlL]+|U\/L|mg\/[dDlL]+|g\/L|mmol\/L|µmol\/L|IU\/L|%)/i, '')
          .trim();

        const isDecimal  = /^\d+[,\.]\d+$/.test(stripped);
        const isSmallInt = /^\d{1,3}$/.test(stripped);
        const isDash     = stripped === '-';

        if (!isDecimal && !isSmallInt && !isDash) return;
        if (colDefs.length === 0) return;

        const nearest  = findNearestCol(colDefs, cell.x);
        const paramKey = resolveParamKey(nearest.text);

        if (data[orderId][paramKey] === undefined) {
          data[orderId][paramKey] = isDash ? null
            : parseFloat(stripped.replace(',', '.'));
          console.log(`  → ${paramKey} = ${data[orderId][paramKey]} (cell '${cell.text}' → '${stripped}', col '${nearest.text}')`);
        }
      });
    }
  }

  console.log('[BulkLabImport] Parsed data:', data);
  return data;
}

function calcStatus(value, refMin, refMax) {
  const num = parseRefBound(value);
  const min = parseRefBound(refMin);
  const max = parseRefBound(refMax);
  if (num === null || min === null || max === null) return 'unknown';
  if (num < min) return 'low';
  if (num > max) return 'high';
  return 'normal';
}

// ── Build extractedParameters array for Firestore ──────────────────────────
function buildParams(parsedRow, refLookup = {}, species = '') {
  const catalogSpecies = SPECIES_ALIAS[species] || species;
  const params = [];
  Object.entries(parsedRow).forEach(([code, rawVal]) => {
    const def = PARAM_MAP[code];
    if (!def) return;
    const refRanges = refLookup[norm(def.name)] || {};
    const r = refRanges[catalogSpecies] || refRanges['default'] || {};
    const refMin = parseRefBound(r.min);
    const refMax = parseRefBound(r.max);
    const status = calcStatus(rawVal, refMin, refMax);
    params.push({
      paramName: def.name,
      code,
      value:  rawVal !== null ? String(rawVal) : '-',
      unit:   def.unit,
      refMin,
      refMax,
      status,
      category: 'General',
    });
  });

  // Calculated: GLOBULINAS & ALB/GLOB
  const prot = parsedRow['T-PROT B (SER)'];
  const alb  = parsedRow['ALB (SER)'];
  if (prot != null && alb != null) {
    const glob    = Math.round((prot - alb) * 100) / 100;
    const albGlob = glob > 0 ? Math.round((alb / glob) * 100) / 100 : null;
    params.push({ paramName: 'GLOBULINAS',    code: 'CALC_GLOBULINAS', value: String(glob),    unit: 'g/dL', refMin: null, refMax: null, status: 'unknown', category: 'General' });
    if (albGlob != null) {
      params.push({ paramName: 'ALB/GLOBULINAS', code: 'CALC_ALB_GLOB', value: String(albGlob), unit: '', refMin: null, refMax: null, status: 'unknown', category: 'General' });
    }
  }

  // Calculated: NUS = UREMIA × 0.467 (BUN = Blood Urea Nitrogen)
  const uremia = parsedRow['UREA (SER)'];
  if (uremia != null) {
    const nus = Math.round(uremia * 0.467 * 100) / 100;
    params.push({ paramName: 'NUS', code: 'CALC_NUS', value: String(nus), unit: 'mg/dL', refMin: null, refMax: null, status: 'unknown', category: 'General' });
  }

  return params;
}

// ── Component ───────────────────────────────────────────────────────────────
export default function BulkLabImport() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { filterByBranch } = useBranch();

  const [file,        setFile]        = useState(null);
  const [dragOver,    setDragOver]    = useState(false);
  const [parsing,     setParsing]     = useState(false);
  const [parseError,  setParseError]  = useState('');
  const [parsedData,  setParsedData]  = useState(null); // { orderId: {code:val} }
  const [matchedOrds, setMatchedOrds] = useState(null); // { orderId: labOrder|null }
  const [importing,   setImporting]   = useState(false);
  const [importLog,   setImportLog]   = useState([]);
  const [done,        setDone]        = useState(false);
  const [snackbar,    setSnackbar]    = useState({ open: false, message: '', severity: 'success' });

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f?.type === 'application/pdf') setFile(f);
    else setSnackbar({ open: true, message: 'Solo se aceptan archivos PDF.', severity: 'warning' });
  }, []);

  // ── Parse PDF ─────────────────────────────────────────────────────────────
  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setParseError('');
    setParsedData(null);
    setMatchedOrds(null);
    setImportLog([]);
    setDone(false);

    try {
      const raw = await parseBiochemPDF(file);

      if (Object.keys(raw).length === 0) {
        setParseError('No se encontraron datos en el PDF. Verifica que sea un archivo del analizador Bioquímica.');
        setParsing(false);
        return;
      }

      // Match orders in Firestore
      const snap = await getDocs(collection(db, 'labOrders'));
      const ordersBySuffix = {};
      filterByBranch(snap.docs.map(d => ({ id: d.id, ...d.data() })), { includeUnassigned: true }).forEach(orderItem => {
        const suffix = (orderItem.orderNumber || '').split('-').pop();
        ordersBySuffix[suffix] = orderItem;
      });

      const matched = {};
      Object.keys(raw).forEach(id => {
        matched[id] = ordersBySuffix[id] || null;
      });

      setParsedData(raw);
      setMatchedOrds(matched);
    } catch (err) {
      setParseError('Error al procesar el PDF: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  // ── Import to Firestore ───────────────────────────────────────────────────
  const handleImport = async () => {
    if (!parsedData || !matchedOrds) return;
    setImporting(true);
    const log = [];

    const refLookup = {};
    try {
      const examSnap = await getDocs(collection(db, 'examTypes'));
      examSnap.docs.forEach((d) => {
        const data = d.data();
        if ((data.category || '').toLowerCase() !== 'bioquímica') return;
        (data.parameters || []).forEach((p) => {
          if (p.name && p.refRanges) refLookup[norm(p.name)] = p.refRanges;
        });
      });
    } catch (err) {
      console.warn('[BulkLabImport] No se pudieron cargar rangos de referencia:', err);
    }

    for (const [orderId, params] of Object.entries(parsedData)) {
      const order = matchedOrds[orderId];
      if (!order) {
        log.push({ orderId, status: 'skipped', msg: 'Sin orden coincidente' });
        continue;
      }
      try {
        const rawParams = buildParams(params, refLookup, order.patientSpecies || '');
        const orderSuffix = (order.orderNumber || '').split('-').pop() || '';

        // Find the matching Bioquímica exam entry in the order
        const bioquimicaExam = (order.examsRequested || []).find((e) =>
          (e.examTypeName || '').toLowerCase().includes('bioqu')
        );
        const examTypeId = bioquimicaExam?.examTypeId || '';
        const examTypeName = bioquimicaExam?.examTypeName || 'Bioquímica';

        const resultRef = await addDoc(collection(db, 'labResults'), {
          orderId: order.id,
          orderNumber: order.orderNumber || '',
          patientId: order.patientId || '',
          patientName: order.patientName || '',
          patientSpecies: order.patientSpecies || '',
          examCategory: 'Bioquímica',
          examTypeId,
          observations: '',
          status: 'pending_review',
          paramCount: rawParams.length,
          extractedParameters: rawParams,
          importedFrom: file.name,
          uploadedAt: serverTimestamp(),
          uploadedBy: currentUser?.uid || '',
        });

        await addDoc(collection(db, 'labReadings'), {
          patientId: order.patientId || '',
          orderId: order.id,
          orderNumber: order.orderNumber || '',
          orderSuffix,
          examTypeId,
          examTypeName,
          extractedAt: serverTimestamp(),
          source: 'bulk_import',
          parameters: rawParams.map((p) => ({
            paramName: p.code,
            value: p.value,
            unit: p.unit,
          })),
        });

        // Update per-exam status (same pattern as UploadResult.js)
        const allExams = order.examsRequested || [];
        const currentExamResults = {
          ...(order.examResults || {}),
          ...(examTypeId ? { [examTypeId]: { resultId: resultRef.id, status: 'pending_review' } } : {}),
        };
        const allDone = allExams.length > 0 && allExams.every((e) => currentExamResults[e.examTypeId]?.resultId);
        const updatePayload = {
          status: allDone ? 'pending_review' : 'processing',
        };
        if (examTypeId) {
          updatePayload[`examResults.${examTypeId}.resultId`] = resultRef.id;
          updatePayload[`examResults.${examTypeId}.status`]   = 'pending_review';
        }
        await updateDoc(doc(db, 'labOrders', order.id), updatePayload);

        log.push({ orderId, status: 'ok', msg: order.orderNumber, resultId: resultRef.id });
      } catch (err) {
        log.push({ orderId, status: 'error', msg: err.message });
      }
    }

    setImportLog(log);
    setDone(true);
    setImporting(false);
  };

  const matchedCount = matchedOrds
    ? Object.values(matchedOrds).filter(Boolean).length
    : 0;
  const totalParsed  = parsedData ? Object.keys(parsedData).length : 0;

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')}
          sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>
          Volver
        </Button>
        <ScienceIcon sx={{ color: LAB_PRIMARY }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>
            Carga Masiva — Bioquímica
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Importación desde analizador bioquímico (PDF)
          </Typography>
        </Box>
      </Box>

      {/* Step 1: Upload */}
      {!parsedData && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #E2E8F0', maxWidth: 640, mx: 'auto' }}>
          <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
            1. Seleccionar archivo PDF del analizador
          </Typography>

          {/* Drop zone */}
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => document.getElementById('pdfInput').click()}
            sx={{
              border: `2px dashed ${dragOver ? LAB_TEAL : '#CBD5E1'}`,
              borderRadius: 2, p: 5, textAlign: 'center', cursor: 'pointer',
              bgcolor: dragOver ? '#F0FDF4' : '#FAFAFA',
              transition: 'all .2s',
              '&:hover': { borderColor: LAB_TEAL, bgcolor: '#F0FDF4' },
            }}>
            <UploadFileIcon sx={{ fontSize: 48, color: file ? LAB_TEAL : '#94A3B8', mb: 1 }} />
            <Typography fontWeight={600} color={file ? LAB_PRIMARY : 'text.secondary'}>
              {file ? file.name : 'Arrastra el PDF aquí o haz clic para seleccionar'}
            </Typography>
            {file && (
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
            )}
            <input id="pdfInput" type="file" accept="application/pdf" hidden
              onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          </Box>

          {parseError && <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>}

          <Button fullWidth variant="contained" size="large" sx={{ mt: 3, bgcolor: LAB_PRIMARY, fontWeight: 700 }}
            disabled={!file || parsing} onClick={handleParse}
            startIcon={parsing ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}>
            {parsing ? 'Procesando PDF…' : 'Analizar PDF'}
          </Button>
          {parsing && <LinearProgress sx={{ mt: 1 }} />}
        </Paper>
      )}

      {/* Step 2: Preview */}
      {parsedData && !done && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
                2. Vista previa — {totalParsed} pacientes encontrados
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {matchedCount} coinciden con órdenes existentes · {totalParsed - matchedCount} sin coincidencia
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" sx={{ borderColor: '#CBD5E1', color: 'text.secondary' }}
                onClick={() => { setParsedData(null); setMatchedOrds(null); setFile(null); }}>
                Cancelar
              </Button>
              <Button variant="contained" disabled={matchedCount === 0 || importing}
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleImport}
                sx={{ bgcolor: LAB_TEAL, fontWeight: 700 }}>
                {importing ? 'Importando…' : `Importar ${matchedCount} resultado${matchedCount !== 1 ? 's' : ''}`}
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 520, border: '1px solid #E2E8F0', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: '#F8FAFC' }}>ID PDF</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: '#F8FAFC' }}>Orden</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: '#F8FAFC' }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: '#F8FAFC' }}>Estado</TableCell>
                  {Object.keys(PARAM_MAP).map(k => (
                    <TableCell key={k} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: '#F8FAFC', whiteSpace: 'nowrap' }}>
                      {PARAM_MAP[k].name}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: '#F8FAFC' }}>GLOBULINAS</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: '#F8FAFC' }}>ALB/GLOB</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(parsedData).map(([orderId, params]) => {
                  const order = matchedOrds[orderId];
                  const prot = params['T-PROT B (SER)'];
                  const alb  = params['ALB (SER)'];
                  const glob = (prot != null && alb != null) ? Math.round((prot - alb) * 100) / 100 : null;
                  const albGlob = (glob != null && glob > 0) ? Math.round((alb / glob) * 100) / 100 : null;

                  return (
                    <TableRow key={orderId} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: LAB_PRIMARY }}>
                        {orderId}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {order ? order.orderNumber : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {order ? order.patientName : '—'}
                      </TableCell>
                      <TableCell>
                        {order ? (
                          <Chip size="small" label="✓ Coincide" sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: '0.72rem' }} />
                        ) : (
                          <Tooltip title="No se encontró una orden con este número">
                            <Chip size="small" label="Sin orden" sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 700, fontSize: '0.72rem' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      {Object.keys(PARAM_MAP).map(k => (
                        <TableCell key={k} sx={{ fontSize: '0.78rem', color: params[k] != null ? LAB_PRIMARY : '#94A3B8' }}>
                          {params[k] != null ? params[k] : '—'}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontSize: '0.78rem', color: glob != null ? LAB_PRIMARY : '#94A3B8' }}>
                        {glob != null ? glob : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: albGlob != null ? LAB_PRIMARY : '#94A3B8' }}>
                        {albGlob != null ? albGlob : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Step 3: Results */}
      {done && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #E2E8F0', maxWidth: 700, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: LAB_TEAL }} />
            <Typography variant="h6" fontWeight={700} color={LAB_PRIMARY} mt={1}>
              Importación completada
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID PDF</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Orden</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Resultado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importLog.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: 700, color: LAB_PRIMARY }}>{row.orderId}</TableCell>
                    <TableCell>{row.msg}</TableCell>
                    <TableCell>
                      {row.status === 'ok'      && <Chip size="small" icon={<CheckCircleIcon />} label="Importado"   sx={{ bgcolor: '#DCFCE7', color: '#16A34A' }} />}
                      {row.status === 'skipped' && <Chip size="small" icon={<WarningAmberIcon />} label="Sin orden" sx={{ bgcolor: '#FEF3C7', color: '#D97706' }} />}
                      {row.status === 'error'   && <Chip size="small" icon={<ErrorIcon />} label="Error"           sx={{ bgcolor: '#FEE2E2', color: '#DC2626' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/lab/orders')}
              sx={{ borderColor: LAB_PRIMARY, color: LAB_PRIMARY, fontWeight: 700 }}>
              Ver órdenes
            </Button>
            <Button variant="contained"
              onClick={() => { setFile(null); setParsedData(null); setMatchedOrds(null); setImportLog([]); setDone(false); }}
              sx={{ bgcolor: LAB_TEAL, fontWeight: 700 }}>
              Nueva importación
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
